// eslint-disable-next-line no-restricted-imports -- this file defines the abstraction
import type {
    CellContext as CellContextCore,
    CellData,
    Column as ColumnCore,
    ColumnDef as ColumnDefCore,
    ColumnHelper as ColumnHelperCore,
    ColumnPinningState,
    DisplayColumnDef as DisplayColumnDefCore,
    ColumnMeta as GlobalColumnMeta,
    Header as HeaderCore,
    OnChangeFn,
    Row as RowCore,
    RowData,
    SortingState,
    TableFeatures as TableFeaturesCore,
    TableOptions,
} from '@tanstack/react-table';
// eslint-disable-next-line no-restricted-imports -- this file defines the abstraction
import {
    columnFilteringFeature,
    columnPinningFeature,
    columnSizingFeature,
    createColumnHelper as createColumnHelperCore,
    createFilteredRowModel,
    createSortedRowModel,
    flexRender,
    globalFilteringFeature,
    metaHelper,
    rowSelectionFeature,
    rowSortingFeature,
    sortFn_alphanumeric,
    sortFn_datetime,
    sortFn_text,
    tableFeatures,
    useTable,
} from '@tanstack/react-table';
import { isEqual, isUndefined, omitBy } from 'lodash-es';
import type { AriaAttributes, CSSProperties, ComponentPropsWithRef, ReactElement, ReactNode } from 'react';
import { memo, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import {
    Table,
    TableBody,
    TableCell,
    TableCellEmpty,
    TableCellLoading,
    TableHead,
    TableHeader,
    TableRow,
    TableSortHeader,
} from '../primitives/table.tsx';
import {
    InfoHint,
    InfoHintBody,
    InfoHintContent,
    InfoHintHeader,
    InfoHintTitle,
    InfoHintTrigger,
} from './info-hint.tsx';

export type SortDirection = 'asc' | 'desc';

export type ColumnSort<TSortBy> = {
    by: TSortBy;
    direction: SortDirection;
};

export type ColumnMeta<TSummary = never, TSortBy = never> = GlobalColumnMeta<TableFeaturesCore, RowData> & {
    align?: 'left' | 'right';
    preserveOpacityWhenDimmed?: boolean;
    enabled?: boolean;
    pinned?: 'left' | 'right';
    summary?: (data: TSummary) => ReactNode;
    sortBy?: TSortBy;
    hint?: ReactNode;
    onHintOpen?: () => void;
};

export const dataTableFeatures = tableFeatures({
    columnFilteringFeature,
    columnPinningFeature,
    columnSizingFeature,
    globalFilteringFeature,
    rowSelectionFeature,
    rowSortingFeature,
    columnMeta: metaHelper<ColumnMeta>(),
    filteredRowModel: createFilteredRowModel(),
    sortedRowModel: createSortedRowModel(),
    sortFns: {
        alphanumeric: sortFn_alphanumeric,
        datetime: sortFn_datetime,
        text: sortFn_text,
    },
});

type TableFeatures<TSummary = never, TSortBy = never, TExtraColumnMeta extends object = object> = Omit<
    typeof dataTableFeatures,
    'columnMeta'
> & {
    columnMeta: ColumnMeta<TSummary, TSortBy> & TExtraColumnMeta;
};

export type ColumnDef<
    TData extends RowData,
    TSummary = never,
    TSortBy = never,
    TExtraColumnMeta extends object = object,
    TValue extends CellData = CellData,
> = ColumnDefCore<TableFeatures<TSummary, TSortBy, TExtraColumnMeta>, TData, TValue>;

export type Column<
    TData extends RowData,
    TSummary = never,
    TSortBy = never,
    TExtraColumnMeta extends object = object,
    TValue extends CellData = CellData,
> = ColumnCore<TableFeatures<TSummary, TSortBy, TExtraColumnMeta>, TData, TValue>;

export type Row<
    TData extends RowData,
    TSummary = never,
    TSortBy = never,
    TExtraColumnMeta extends object = object,
> = RowCore<TableFeatures<TSummary, TSortBy, TExtraColumnMeta>, TData>;

export type CellContext<
    TData extends RowData,
    TSummary = never,
    TSortBy = never,
    TExtraColumnMeta extends object = object,
    TValue extends CellData = CellData,
> = CellContextCore<TableFeatures<TSummary, TSortBy, TExtraColumnMeta>, TData, TValue>;

export type DisplayColumnDef<
    TData extends RowData,
    TSummary = never,
    TSortBy = never,
    TExtraColumnMeta extends object = object,
    TValue extends CellData = CellData,
> = DisplayColumnDefCore<TableFeatures<TSummary, TSortBy, TExtraColumnMeta>, TData, TValue>;

export type ColumnHelper<
    TData extends RowData,
    TSummary = never,
    TSortBy = never,
    TExtraColumnMeta extends object = object,
> = ColumnHelperCore<TableFeatures<TSummary, TSortBy, TExtraColumnMeta>, TData>;

export function createColumnHelper<
    TData extends RowData,
    TSummary = never,
    TSortBy = never,
    TExtraColumnMeta extends object = object,
>(): ColumnHelper<TData, TSummary, TSortBy, TExtraColumnMeta> {
    return createColumnHelperCore<TableFeatures<TSummary, TSortBy, TExtraColumnMeta>, TData>();
}

// eslint-disable-next-line no-restricted-imports -- this file defines the abstraction
export type {
    AccessorFn,
    CellData,
    ColumnPinningState,
    FilterFnOption,
    OnChangeFn,
    RowData,
    RowSelectionState,
    SortingState,
    Updater,
} from '@tanstack/react-table';

type SupportedTableOptions<TData extends RowData, TSummary, TSortBy, TExtraColumnMeta extends object> = Pick<
    TableOptions<TableFeatures<TSummary, TSortBy, TExtraColumnMeta>, TData>,
    | 'defaultColumn'
    | 'enableMultiRowSelection'
    | 'globalFilterFn'
    | 'manualSorting'
    | 'onGlobalFilterChange'
    | 'onRowSelectionChange'
> & {
    state?: Omit<
        NonNullable<TableOptions<TableFeatures<TSummary, TSortBy, TExtraColumnMeta>, TData>['state']>,
        'sorting'
    >;
};

type DataTableProps<
    TData extends RowData,
    TSummary,
    TSortBy,
    TExtraColumnMeta extends object,
> = SupportedTableOptions<TData, TSummary, TSortBy, TExtraColumnMeta> &
    Omit<ComponentPropsWithRef<'table'>, 'summary'> & {
        columns: readonly ColumnDef<TData, TSummary, TSortBy, TExtraColumnMeta>[];
        data: readonly TData[] | undefined;
        emptyLabel: ReactNode;
        isLoading?: boolean;
        totalRowsCount?: number;
        onRowClick?: (row: Row<TData, TSummary, TSortBy, TExtraColumnMeta>) => void;
        disableHeader?: boolean;
        summary?: TSummary;
        dimNonSelectedRows?: boolean;
        isFetchingNextPage?: boolean;
        hasNextPage?: boolean;
        onFetchNextPage?: () => void;
        sorting?: ColumnSort<TSortBy>;
        onSortingChange?: (sort: ColumnSort<TSortBy> | undefined, columnId: string | undefined) => void;
    };

const emptyData: [] = [];

export function DataTable<
    TData extends RowData,
    TSummary = never,
    TSortBy = never,
    TExtraColumnMeta extends object = object,
>({
    disableHeader = false,
    emptyLabel,
    isLoading = false,
    totalRowsCount = Number.POSITIVE_INFINITY,
    onRowClick,
    data,
    isFetchingNextPage,
    onFetchNextPage,
    hasNextPage,
    summary,
    dimNonSelectedRows = false,
    columns,
    state,
    defaultColumn,
    enableMultiRowSelection,
    globalFilterFn,
    manualSorting,
    onGlobalFilterChange,
    onRowSelectionChange,
    sorting,
    onSortingChange,
    ref,
    ...tableProps
}: DataTableProps<TData, TSummary, TSortBy, TExtraColumnMeta>): ReactElement {
    const isSortingControlled = onSortingChange !== undefined;

    const enabledColumns = useMemo(
        () =>
            columns
                .filter((column) => column.meta?.enabled !== false)
                .map((column) => ({
                    ...column,
                    enableSorting: isSortingControlled
                        ? column.meta?.sortBy !== undefined
                        : column.enableSorting === true,
                })),
        [columns, isSortingControlled],
    );

    const sortingState = useMemo<SortingState | undefined>(() => {
        if (sorting === undefined) {
            return isSortingControlled ? [] : undefined;
        }

        const column = enabledColumns.find(
            (candidate) => candidate.meta?.sortBy !== undefined && isEqual(candidate.meta.sortBy, sorting.by),
        );
        const columnId = column && resolveColumnId(column);

        return columnId === undefined ? [] : [{ id: columnId, desc: sorting.direction === 'desc' }];
    }, [enabledColumns, isSortingControlled, sorting]);

    const handleSortingChange: OnChangeFn<SortingState> | undefined =
        onSortingChange &&
        ((updater) => {
            const [next] = typeof updater === 'function' ? updater(sortingState ?? []) : updater;
            const by = enabledColumns.find((column) => resolveColumnId(column) === next?.id)?.meta?.sortBy;

            onSortingChange(
                next && by !== undefined ? { by, direction: next.desc ? 'desc' : 'asc' } : undefined,
                next?.id,
            );
        });

    const columnPinning = useMemo<ColumnPinningState>(
        () => ({
            start: pinnedColumnIds(enabledColumns, 'left'),
            end: pinnedColumnIds(enabledColumns, 'right'),
        }),
        [enabledColumns],
    );

    const table = useTable<TableFeatures<TSummary, TSortBy, TExtraColumnMeta>, TData>({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- It's safe because columnMeta is just a type helper
        features: dataTableFeatures as TableFeatures<TSummary, TSortBy, TExtraColumnMeta>,
        ...omitBy(
            {
                defaultColumn,
                enableMultiRowSelection,
                globalFilterFn,
                manualSorting: manualSorting ?? (isSortingControlled || undefined),
                onGlobalFilterChange,
                onRowSelectionChange,
                onSortingChange: handleSortingChange,
            },
            isUndefined,
        ),
        columns: enabledColumns,
        state: {
            columnPinning,
            ...omitBy({ sorting: sortingState }, isUndefined),
            ...omitBy(state, isUndefined),
        },
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- readonly widening on data prop
        data: (data ?? emptyData) as TData[],
    });

    const leafColumns = (table.getHeaderGroups().at(-1)?.headers ?? []).map((header) => header.column);
    const columnsCount = leafColumns.length;
    const rowCount = table.getRowModel().rows.length || 0;
    const isEmptyTable = rowCount === 0;
    const hasSelectedVisibleRow = table.getSelectedRowModel().rows.length > 0;

    const summaryRow = useMemo(() => {
        if (summary === undefined) {
            return undefined;
        }

        const cells: Record<string, ReactNode> = {};
        for (const column of enabledColumns) {
            const columnId = resolveColumnId(column);
            if (columnId !== undefined && column.meta?.summary !== undefined) {
                cells[columnId] = column.meta.summary(summary);
            }
        }
        return cells;
    }, [enabledColumns, summary]);

    const { ref: lastRowToFetchRef } = useInView({
        threshold: 0.1,
        onChange: (inView) => {
            if (inView && onFetchNextPage && !isFetchingNextPage && hasNextPage) {
                onFetchNextPage();
            }
        },
        skip: isEmptyTable,
    });

    return (
        <Table
            {...tableProps}
            ref={ref}
            style={{ minWidth: isEmptyTable ? undefined : table.getCenterTotalSize() }}
        >
            {!disableHeader && (
                <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                                return (
                                    <TableHead
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        style={getCommonPinningStyles(header.column)}
                                        align={
                                            header.column.columnDef.meta?.align === 'right' ? 'right' : 'left'
                                        }
                                        aria-sort={getAriaSort(header.column)}
                                    >
                                        {header.isPlaceholder || isLoading || isEmptyTable ? null : (
                                            <ColumnHeader header={header} />
                                        )}
                                    </TableHead>
                                );
                            })}
                        </TableRow>
                    ))}

                    {summaryRow && (
                        <TableRow>
                            {leafColumns.map((column) => {
                                const value = summaryRow[column.id];
                                const align = column.columnDef.meta?.align === 'right' ? 'right' : 'left';

                                return (
                                    <TableHead
                                        key={column.id}
                                        align={align}
                                        style={getCommonPinningStyles(column)}
                                        className="bg-white font-bold text-neutral-950"
                                    >
                                        {value}
                                    </TableHead>
                                );
                            })}
                        </TableRow>
                    )}
                </TableHeader>
            )}

            <TableBody>
                {!isLoading && !isEmptyTable
                    ? table.getRowModel().rows.map((row, idx, arr) => {
                          const isDimmed =
                              dimNonSelectedRows && hasSelectedVisibleRow && !row.getIsSelected();

                          return (
                              <TableRow
                                  key={row.id}
                                  ref={
                                      // This can improve UX by fetching in the future
                                      idx === arr.length - (totalRowsCount === arr.length ? 1 : 10)
                                          ? lastRowToFetchRef
                                          : undefined
                                  }
                                  data-state={row.getIsSelected() && 'selected'}
                                  data-dimmed={isDimmed ? 'true' : undefined}
                                  onClick={
                                      onRowClick
                                          ? () => {
                                                onRowClick(row);
                                            }
                                          : undefined
                                  }
                              >
                                  {leafColumns.map((column) => {
                                      const cell = row.getAllCellsByColumnId()[column.id];
                                      if (cell === undefined) {
                                          return null;
                                      }
                                      const meta = cell.column.columnDef.meta;
                                      const dimCell = isDimmed && !meta?.preserveOpacityWhenDimmed;
                                      return (
                                          <TableCell
                                              key={cell.id}
                                              data-state={row.getIsSelected() && 'selected'}
                                              className={
                                                  cell.column.getIsPinned()
                                                      ? 'bg-white group-hover:bg-neutral-25'
                                                      : ''
                                              }
                                              style={getCommonPinningStyles(cell.column, dimCell)}
                                              align={meta?.align === 'right' ? 'right' : 'left'}
                                          >
                                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                          </TableCell>
                                      );
                                  })}
                              </TableRow>
                          );
                      })
                    : null}

                {isLoading || isFetchingNextPage ? (
                    <TableCellLoading colSpan={columnsCount} className={isFetchingNextPage ? 'h-32' : ''} />
                ) : null}

                {!isLoading && isEmptyTable ? (
                    <TableCellEmpty colSpan={columnsCount}>{emptyLabel}</TableCellEmpty>
                ) : null}
            </TableBody>
        </Table>
    );
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- memo doesn't change the component type
export const MemoDataTable = memo(DataTable) as typeof DataTable;

type ColumnHeaderProps<TData extends RowData, TSummary, TSortBy, TExtraColumnMeta extends object> = {
    header: HeaderCore<TableFeatures<TSummary, TSortBy, TExtraColumnMeta>, TData>;
};

function ColumnHeader<TData extends RowData, TSummary, TSortBy, TExtraColumnMeta extends object>({
    header,
}: ColumnHeaderProps<TData, TSummary, TSortBy, TExtraColumnMeta>): ReactNode {
    const { column } = header;
    const label = flexRender(column.columnDef.header, header.getContext());
    const meta = column.columnDef.meta;
    const hint =
        meta?.hint === undefined ? undefined : (
            <InfoHint
                onOpenChange={(open) => {
                    if (open) {
                        meta.onHintOpen?.();
                    }
                }}
            >
                <InfoHintTrigger />
                <InfoHintContent side="bottom">
                    <InfoHintHeader>
                        <InfoHintTitle>{label}</InfoHintTitle>
                    </InfoHintHeader>
                    <InfoHintBody>{meta.hint}</InfoHintBody>
                </InfoHintContent>
            </InfoHint>
        );

    if (!column.getCanSort()) {
        return hint === undefined ? (
            label
        ) : (
            <span className="inline-flex items-center gap-tight align-middle">
                {label}
                {hint}
            </span>
        );
    }

    const sorted = column.getIsSorted();

    return (
        <TableSortHeader
            title={label}
            align={meta?.align}
            direction={sorted === false ? undefined : sorted}
            onDirectionChange={(direction) => {
                column.toggleSorting(direction === 'desc');
            }}
        >
            {hint}
        </TableSortHeader>
    );
}

function getAriaSort<TData extends RowData, TSummary, TSortBy, TExtraColumnMeta extends object>(
    column: Column<TData, TSummary, TSortBy, TExtraColumnMeta>,
): AriaAttributes['aria-sort'] {
    if (!column.getCanSort()) {
        return undefined;
    }
    switch (column.getIsSorted()) {
        case 'asc':
            return 'ascending';
        case 'desc':
            return 'descending';
        case false:
            return 'none';
    }
}

function pinnedColumnIds<TData extends RowData, TSummary, TSortBy, TExtraColumnMeta extends object>(
    columns: ColumnDef<TData, TSummary, TSortBy, TExtraColumnMeta>[],
    side: 'left' | 'right',
): string[] {
    const ids: string[] = [];
    for (const column of columns) {
        if (column.meta?.pinned === side) {
            const id = resolveColumnId(column);
            if (id !== undefined) {
                ids.push(id);
            }
        }
    }
    return ids;
}

function resolveColumnId<TData extends RowData, TSummary, TSortBy, TExtraColumnMeta extends object>(
    column: ColumnDef<TData, TSummary, TSortBy, TExtraColumnMeta>,
): string | undefined {
    if (column.id !== undefined) {
        return column.id;
    }
    if ('accessorKey' in column) {
        return String(column.accessorKey);
    }
    if (typeof column.header === 'string') {
        return column.header;
    }
    return undefined;
}

function getCommonPinningStyles<TData extends RowData, TSummary, TSortBy, TExtraColumnMeta extends object>(
    column: Column<TData, TSummary, TSortBy, TExtraColumnMeta>,
    isDimmed = false,
): CSSProperties {
    const isPinned = column.getIsPinned();
    let opacity = 1;
    if (isPinned) {
        opacity = 0.95;
    }
    if (isDimmed) {
        opacity = 0.5;
    }
    return {
        left: isPinned === 'start' ? `${column.getStart('start')}px` : undefined,
        right: isPinned === 'end' ? `${column.getAfter('end')}px` : undefined,
        opacity,
        position: isPinned ? 'sticky' : 'relative',
        width: column.getSize(),
        zIndex: isPinned ? 1 : 0,
    };
}
